import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Menu, TableProps } from "@mantine/core";
import { CurrencyCode, Order, OrderTabs, VisibleColumnSpec } from "@/types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useGetOrdersForCompany } from "@/services/Orders";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Tag from "../../atoms/Tag";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import Group from "@/components/atoms/Group";
import Text from "@/components/atoms/Text";
import SortableHeader from "@/components/molecules/SortableHeader";
import DataTable from "@/components/molecules/DataTable";
import { useAuth } from "@/contexts/AuthContext";
import NumberFormatter from "../../atoms/NumberFormatter";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useOrderStore } from "@/stores/ordersStore";
import { useOrdersDrawerStore } from "@/stores/ordersDrawerStore";
import { useNavigate } from "react-router-dom";
dayjs.extend(advancedFormat);

interface OrdersTableProps extends TableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<Order, "actions" | "icon" | "product" | "lastPrice">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param actionable - Boolean to enable or disable row actions
 * @param initialRowsPerPage - set maximum number of rows to display used for snapshots
 *
 **/
const OrdersTable: FC<OrdersTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination: _ = true,
  actionable: _1 = true,
  initialRowsPerPage = 15,
}) => {
  const { t } = useTranslation();

  const { currentCompany, user } = useAuth();
  const openOrdersDrawer = useOrdersDrawerStore((s) => s.open);
  const sortDirection = useOrderStore((state) => state.sortDirection);
  const sortBy = useOrderStore((state) => state.sortBy);
  const includeInactive = useOrderStore((state) => state.includeInactive);
  const statuses = useOrderStore((state) => state.statuses);
  const limit = useOrderStore((state) => state.limit);
  const currentPage = useOrderStore((state) => state.currentPage);
  const setCurrentPage = useOrderStore((state) => state.setPage);
  const setNumberOfPages = useOrderStore((state) => state.setNumberOfPages);
  const numberOfPages = useOrderStore((state) => state.numberOfPages);
  const setLimit = useOrderStore((state) => state.setLimit);
  const orderTypes = useOrderStore((state) => state.orderTypes);
  const onlyPromoted = useOrderStore((state) => state.onlyPromoted);
  const awaitingProccessingOnly = useOrderStore((state) => state.awaitingProccessingOnly);
  const salesItemId = useOrderStore((state) => state.salesItemId);
  const excludeContractOrders = useOrderStore((state) => state.excludeContractOrders);
  const navigate = useNavigate();

  const { data: orders, isLoading } = useGetOrdersForCompany(
    {
      companyId: currentCompany?.companyId as number,
      includeInactive,
      pageNumber: currentPage,
      excludeContractOrders: excludeContractOrders,
      limit: limit,
      sortBy: sortBy,
      sortDirection: sortDirection,
      statuses: statuses,
      orderTypes: orderTypes,
      onlyPromoted: onlyPromoted,
      awaitingProcessingOnly: awaitingProccessingOnly ? true : null,
      salesItemId: salesItemId ? Number(salesItemId) : null,
    },
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  useEffect(() => {
    if (!orders) return;
    setNumberOfPages(orders.numberOfPages);
  }, [orders?.numberOfPages]);

  const openDrawerHandler = (orderId: number, tab: OrderTabs) => {
    openOrdersDrawer({ orderId, tab });
  };

  const baseColumns: ColumnDef<Order>[] = useMemo(
    () => [
      {
        header: () => <></>,
        id: "icon",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          if (row.original.status === "REQUIRES_ACTION" || row.original.status === "PENDING_TRANSPORT_SELECTION") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name="yellow-exclamation" />
              </Group>
            );
          } else if (row.original.orderType.type === "BUY") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"purchaseIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else if (row.original.orderType.type === "SELL") {
            return (
              <Group align={"center"} justify={"center"}>
                <SVGAsset name={"saleIndicator"} style={{ width: "1.5rem" }} />
              </Group>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:id")} />,
        id: "id",
        accessorKey: "id",
        cell: ({ row }) => {
          return row.original.id;
        },
        size: 30,
        minSize: 30,
        maxSize: 30,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:date")} />,
        id: "created",
        accessorKey: "created",
        cell: ({ row }) => {
          return dayjs(row.original.created).format("Do MMMM YYYY, HH:mm");
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:type")} />,
        id: "salesItem",
        accessorKey: "salesItem.salesItem",
        cell: ({ row }) => {
          if (user?.language.id) {
            return row.original.salesItem.nameTranslated[user.language.id];
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:amount")} />,
        id: "amount",
        accessorKey: "amount",
        cell: ({ row }) => {
          const amountDeviation = row.original.amountDeviation ?? 0;
          return (
            <Text ta="right">
              <NumberFormatter value={row.original.amount} suffix={` ${row.original.unit.unit}`} />
              {amountDeviation > 0 ? ` +/- ${amountDeviation}%` : null}
            </Text>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("common:remainingAmount")} />,
        id: "remainingAmount",
        accessorKey: "remainingAmount",
        minSize: 200,
        maxSize: 200,
        cell: ({ row }) => {
          return (
            <Text ta="right">
              {row.original.remainingAmount && row.original.remainingAmount > 0 ? (
                <NumberFormatter value={row.original.remainingAmount} suffix={` ${row.original.unit.unit}`} />
              ) : (
                "-"
              )}
            </Text>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader ta="right" column={column} label={t("orders:finalAmount")} />,
        id: "finalAmount",
        accessorKey: "finalAmount",
        minSize: 180,
        maxSize: 180,
        cell: ({ row }) => {
          const amountDeviation = row.original.amountDeviation ?? 0;
          const finalAmountString =
            row.original.finalAmount && row.original.finalAmount > 0 && amountDeviation > 0
              ? row.original.status === "AWAITING_FINAL_AMOUNT"
                ? "-"
                : `${row.original.finalAmount} ${row.original.unit.unit}`
              : "-";
          return (
            <Text ta="right" truncate={true}>
              {finalAmountString}
            </Text>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("dashboard:status")} />,
        id: "status",
        accessorKey: "status",
        cell: ({ row }) => {
          return (
            <Tag status={row.original.status}>{t("enumerables:orderStatus." + row.original.status?.toUpperCase())}</Tag>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("orders:lastPrice")} />,
        id: "lastPrice",
        accessorFn: (row) => {
          const val = row.price?.[currentCompany?.companyCurrency as CurrencyCode];
          return val ? Number(val) : 0;
        },
        cell: ({ row }) => {
          const lastPrice = row.original.price?.[currentCompany?.companyCurrency as CurrencyCode];
          return (
            <>
              {lastPrice && lastPrice > 0 ? (
                <NumberFormatter
                  value={lastPrice.toFixed(2)}
                  suffix={` ${currentCompany?.companyCurrency}/${row.original.priceUnit?.unit}`}
                />
              ) : (
                "-"
              )}
            </>
          );
        },
      },
      {
        header: ({ column }) => (
          <SortableHeader column={column} label={`${t("common:price")} (${currentCompany?.companyCurrency})`} />
        ),
        id: `totalPriceWithFee.${currentCompany?.companyCurrency}`,
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            row.original.orderType.id === OrderTypeEnum.BUY && (
              <Group justify={"flex-end"} w={"100%"}>
                <Menu offset={0}>
                  <Menu.Target>
                    <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                      <Group align={"center"} justify={"center"}>
                        <SVGAsset name="dotdotdot-grey" />
                      </Group>
                    </span>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {row.original.contractId ? (
                      <Menu.Item
                        style={{ width: "auto" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/contracts/details/" + row.original.contractId);
                        }}
                      >
                        <Group>{t("actions:viewContract")}</Group>
                      </Menu.Item>
                    ) : null}
                    <Menu.Item
                      style={{ width: "auto" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawerHandler(row.original.id, "claims");
                      }}
                    >
                      <Group>
                        <SVGAsset name="yellow-exclamation" />
                        {t("orders:reportProblem")}
                      </Group>
                    </Menu.Item>
                    <Menu.Item
                      style={{ width: "auto" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawerHandler(row.original.id, "deviations");
                      }}
                    >
                      <Group>
                        <SVGAsset name="yellow-exclamation" />
                        {t("contracts:contractDeviation.deviation")}
                      </Group>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )
          );
        },
      },
    ],
    [currentCompany?.companyId, t, currentPage, limit]
  );

  const columns = useVisibleColumns<Order, "actions" | "icon" | "product" | "lastPrice">(baseColumns, visibleColumns);

  return (
    <>
      <DataTable
        data={orders?.orders || []}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={1}
        useServerSidePagination={true}
        showPagination={true}
        onRowsPerPageChange={(rows) => setLimit(Number(rows))}
        onPageChange={(page) => setCurrentPage(page)}
        rowsPerPage={limit}
        activePage={currentPage}
        totalItems={limit * numberOfPages}
        onRowClick={(row) => openOrdersDrawer({ orderId: row.original.id })}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
    </>
  );
};

export default OrdersTable;
